import coreAPIService from './coreAPIService';
import { CTW_NODE_API_ENDPOINT } from '../utils';

/** JavaScript weekday indexes: Sunday = 0 through Saturday = 6. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface SalesCaller {
    orgId: string;
    clientId: string;
    id: string;
    name: string;
    phoneNumber: string;
    isActive: boolean;
    availabilityEnabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
    workingDays: Weekday[];
    createdAt?: string;
    updatedAt?: string;
}

export type SalesCallerInput = Omit<
    SalesCaller,
    'id' | 'createdAt' | 'updatedAt'
>;

export type SalesCallerUpdate = Partial<SalesCallerInput>;

export interface SalesCallerStatusInput {
    isActive: boolean;
}

export interface SalesUrl {
    id: string;
    orgId: string;
    clientId: string;
    name: string;
    url: string;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export type SalesUrlInput = Omit<SalesUrl, 'id' | 'createdAt' | 'updatedAt'>;
export type SalesUrlUpdate = Partial<SalesUrlInput>;

interface ApiResponse<T> {
    meta?: {
        code?: number;
        status?: boolean | number;
        message?: string;
    };
    data?: T | { data?: T };
}

const unwrap = <T>(response: ApiResponse<T>): T => {
    const failed =
        response?.meta?.status === false ||
        (typeof response?.meta?.code === 'number' && response.meta.code >= 400);

    if (failed) {
        throw new Error(response.meta?.message || 'Lead distribution request failed');
    }

    const payload = response?.data;
    if (
        payload &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        'data' in payload
    ) {
        return (payload as { data?: T }).data as T;
    }

    return payload as T;
};

class LeadDistributionService {
    async listCallers(orgId: string, clientId: string): Promise<SalesCaller[]> {
        const response = await coreAPIService.get<ApiResponse<SalesCaller[]>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/callers/${encodeURIComponent(orgId)}/${encodeURIComponent(clientId)}`
        );
        return unwrap(response) || [];
    }

    async createCaller(caller: SalesCallerInput): Promise<SalesCaller> {
        const response = await coreAPIService.post<ApiResponse<SalesCaller>>(
            CTW_NODE_API_ENDPOINT,
            '/lead-distribution/v1/callers',
            caller
        );
        return unwrap(response);
    }

    async updateCaller(
        callerId: string,
        caller: SalesCallerUpdate
    ): Promise<SalesCaller> {
        const response = await coreAPIService.put<ApiResponse<SalesCaller>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/callers/${encodeURIComponent(callerId)}`,
            caller
        );
        return unwrap(response);
    }

    async setCallerEnabled(
        callerId: string,
        isActive: boolean
    ): Promise<SalesCaller> {
        const response = await coreAPIService.patch<ApiResponse<SalesCaller>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/callers/${encodeURIComponent(callerId)}/status`,
            { isActive } satisfies SalesCallerStatusInput
        );
        return unwrap(response);
    }

    async deleteCaller(callerId: string): Promise<void> {
        const response = await coreAPIService.delete<ApiResponse<void>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/callers/${encodeURIComponent(callerId)}`
        );
        unwrap(response);
    }

    async listUrls(orgId: string, clientId: string): Promise<SalesUrl[]> {
        const response = await coreAPIService.get<ApiResponse<SalesUrl[]>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/urls/${encodeURIComponent(orgId)}/${encodeURIComponent(clientId)}`
        );
        return unwrap(response) || [];
    }

    async createUrl(url: SalesUrlInput): Promise<SalesUrl> {
        const response = await coreAPIService.post<ApiResponse<SalesUrl>>(
            CTW_NODE_API_ENDPOINT,
            '/lead-distribution/v1/urls',
            url
        );
        return unwrap(response);
    }

    async updateUrl(urlId: string, url: SalesUrlUpdate): Promise<SalesUrl> {
        const response = await coreAPIService.put<ApiResponse<SalesUrl>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/urls/${encodeURIComponent(urlId)}`,
            url
        );
        return unwrap(response);
    }

    async setUrlStatus(urlId: string, isActive: boolean): Promise<SalesUrl> {
        const response = await coreAPIService.patch<ApiResponse<SalesUrl>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/urls/${encodeURIComponent(urlId)}/status`,
            { isActive }
        );
        return unwrap(response);
    }

    async deleteUrl(urlId: string): Promise<void> {
        const response = await coreAPIService.delete<ApiResponse<void>>(
            CTW_NODE_API_ENDPOINT,
            `/lead-distribution/v1/urls/${encodeURIComponent(urlId)}`
        );
        unwrap(response);
    }

}

const leadDistributionService = new LeadDistributionService();

export default leadDistributionService;
